import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { REDUX_STATUS } from "constants/status";
import { LoadSearchFilterFormData } from "models/Load";
import { IRateTrend } from "models/RateTrend";
import { TruckFilterFormData } from "models/Truck";
import { AuthUserAccount } from "models/User";
import {
  createFactoringUser,
  getAccount,
  getFactoringProfile,
  getInvitedGroups,
  getUserPermission,
  updateFactoringInfo
} from "store/user/actions";
import { getCurrentUser, loginUser, signupUser } from "./actions";
import { IAuthState } from "./types";
import { navigateTo } from "services/NavigationService";

const initialState: IAuthState = {
  status: REDUX_STATUS.INITIAL,
  userToken: null,
  refreshToken: null,
  account: null,
  userDetails: null,
  userPermission: null,
  error: null,
  isDark: undefined
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserToken(state, action: PayloadAction<string>) {
      state.userToken = action.payload;
    },
    logOut: (state) => {
      state.status = REDUX_STATUS.FULFILLED;
      state.userToken = null;
      state.refreshToken = null;
      state.account = null;
      state.userDetails = null;
      state.loadFormFields = null;
      state.truckFormFields = null;
      state.rateTrendFormFields = null;
      state.error = null;
      navigateTo("/", { replace: true });
    },
    setIsDark: (state, action) => {
      state.isDark = action.payload;
    },
    storeRateTrendFormFields: (state, { payload }: { payload: IRateTrend }) => {
      state.rateTrendFormFields = payload;
      if (state.userPermission?.rate_trend_tool_limit_month) {
        state.userPermission.rate_trend_tool_limit_month =
          state.userPermission.rate_trend_tool_limit_month - 1;
      }
    },
    storeLoadFormFields: (state, { payload }: { payload: LoadSearchFilterFormData }) => {
      state.loadFormFields = payload;
    },
    storeTruckFormFields: (state, { payload }: { payload: TruckFilterFormData }) => {
      state.truckFormFields = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        state.userToken = action.payload.access_token;
        state.refreshToken = action.payload.access_token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      // Signup
      .addCase(signupUser.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.status = REDUX_STATUS.FULFILLED;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      // Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        state.account = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      // User Permission
      .addCase(getUserPermission.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(getUserPermission.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        state.userPermission = action.payload;
      })
      .addCase(getUserPermission.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      // User Details
      .addCase(getAccount.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(getAccount.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        if (action.payload)
          state.userDetails = {
            ...state.userDetails,
            allow_bulk_import: action.payload.allow_bulk_import,
            factoring_approved: action.payload.factoring_approved,
            permissions: action.payload.permissions
          } as AuthUserAccount;
      })
      .addCase(getAccount.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      .addCase(createFactoringUser.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        state.userDetails = {
          ...state.userDetails,
          factoring_approved: action.payload.factoring_approved
        } as AuthUserAccount;
      })
      // Update Factoring Info
      .addCase(updateFactoringInfo.pending, (state) => {
        state.status = REDUX_STATUS.PENDING;
        state.error = null;
      })
      .addCase(updateFactoringInfo.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        state.userDetails = {
          ...state.userDetails,
          factoring_approved: action.payload.factoring_approved
        } as AuthUserAccount;
      })
      .addCase(updateFactoringInfo.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      })
      // get invited groups
      .addCase(getInvitedGroups.fulfilled, (state, action) => {
        state.invitedGroupLength = Array.isArray(action.payload) ? action.payload.length : 0;
      })
      .addCase(getInvitedGroups.rejected, (state) => {
        state.invitedGroupLength = undefined;
      })
      // Keep the wallet permission current when the factoring profile refreshes.
      .addCase(getFactoringProfile.fulfilled, (state, action) => {
        state.status = REDUX_STATUS.FULFILLED;
        if (action.payload)
          state.userDetails = {
            ...state.userDetails,
            allow_self_finance_fr: action.payload.allow_self_finance_fr,
            stripe_enabled: action.payload.stripe_enabled
          } as AuthUserAccount;
      })
      .addCase(getFactoringProfile.rejected, (state, action) => {
        state.status = REDUX_STATUS.REJECTED;
        state.error = action.error;
      });
  }
});

export const {
  setUserToken,
  logOut,
  setIsDark: setDefaultIsDark,
  storeRateTrendFormFields,
  storeLoadFormFields,
  storeTruckFormFields
} = authSlice.actions;

export default authSlice.reducer;
